/**
 * Per-tab projections over the one architecture tree (TDD §3.11).
 *
 * The tabs are not separate diagrams. `hierarchy_nodes[]` is the single source of
 * truth — the backend parents every compute and data node to a subnet, so a "Cloud"
 * document that dropped networks would orphan the very nodes it exists to show.
 * Each tab is therefore a filter plus a re-parent, and an edit made in any tab is an
 * edit to the one tree.
 *
 * Classification is read off data the seeder already wrote (`config.tier`,
 * `config.detailLevel`) rather than re-deriving it from service names in JS. The
 * backend owns that classification in `_network_role`, and this repo has been bitten
 * before by the same rules living in two files — see `test_nesting_rules_parity.py`.
 */

export const TABS = [
  {
    id: "cloud",
    label: "Cloud",
    hint: "Application services in the networks that hold them",
  },
  {
    id: "network",
    label: "Network",
    hint: "Address space, subnet tiers, gateways and peering",
  },
  {
    id: "rbac",
    label: "Identity & access",
    hint: "Least-privilege groups, roles, and resource access",
  },
  {
    id: "inventory",
    label: "Inventory",
    hint: "Foundation scopes, resource names, costs and readiness",
  },
];

export const DEFAULT_TAB = "cloud";

const isResource = (n) => n?.kind === "resource";
const tierOf = (n) => (n?.config?.tier || "").toLowerCase();

/** Network plumbing: gateways, route tables, firewalls, and the like. */
export const isNetworkResource = (n) => isResource(n) && tierOf(n) === "networking";

/**
 * Per-availability-zone subnets, which the topology pass adds beyond the tier pair.
 * Real, editable nodes — just too much detail for the Cloud tab, which is the whole
 * reason the tabs exist.
 */
const isNetworkDetail = (n) => n?.config?.detailLevel === "network";

function isHidden(node, tab) {
  if (tab === "network") {
    // Keep every container so the address space reads as a hierarchy, and keep only
    // the resources that are part of the network itself.
    return isResource(node) && !isNetworkResource(node);
  }
  // Cloud: drop the plumbing and the per-AZ detail, keep application services.
  return isNetworkResource(node) || isNetworkDetail(node);
}

/**
 * Project the tree for a tab.
 *
 * Hiding a container would orphan whatever sits under it, so a surviving node whose
 * parent is hidden is re-parented to its nearest visible ancestor. Returns new node
 * objects — never mutates the tree, which is still the thing that gets saved.
 */
export function projectNodes(nodes, tab) {
  const all = Array.isArray(nodes) ? nodes : [];
  if (!tab || tab === "all") return all;

  const byId = new Map(all.map((n) => [n.id, n]));
  const hidden = new Set(all.filter((n) => isHidden(n, tab)).map((n) => n.id));
  if (tab === "cloud" && all.some((n) =>
    n?.config?.networkPlacement?.mode === "managed_public_endpoint")) {
    // App Runner's public endpoint is outside the VPC. A public subnet with no
    // visible application resource is VPC plumbing, not the app's ingress tier.
    for (const subnet of all.filter((n) => n.type === "public-subnet")) {
      const hasAppDescendant = all.some((candidate) => {
        if (!isResource(candidate) || isHidden(candidate, tab)) return false;
        const seen = new Set([candidate.id]);
        let parentId = candidate.parentId;
        while (parentId && !seen.has(parentId)) {
          if (parentId === subnet.id) return true;
          seen.add(parentId);
          parentId = byId.get(parentId)?.parentId ?? null;
        }
        return false;
      });
      if (!hasAppDescendant) hidden.add(subnet.id);
    }
  }

  const nearestVisibleAncestor = (node) => {
    const seen = new Set([node.id]); // a malformed cycle must not hang the render
    let parentId = node.parentId;
    while (parentId && !seen.has(parentId)) {
      if (!hidden.has(parentId)) return parentId;
      seen.add(parentId);
      parentId = byId.get(parentId)?.parentId ?? null;
    }
    return null;
  };

  return all
    .filter((n) => !hidden.has(n.id))
    .map((n) => {
      if (!n.parentId || !hidden.has(n.parentId)) return n;
      return { ...n, parentId: nearestVisibleAncestor(n) };
    });
}

/**
 * Project peer connections for a tab.
 *
 * VPC peering is a network concern and reads as noise on the Cloud tab. Any edge whose
 * endpoints aren't both visible is dropped — a peering line to a node that isn't drawn
 * is worse than no line.
 */
export function projectPeerConnections(peerConnections, visibleNodes, tab) {
  const all = Array.isArray(peerConnections) ? peerConnections : [];
  const visible = new Set((visibleNodes || []).map((n) => n.id));
  return all.filter((pc) => {
    if (tab === "cloud" && pc.kind === "peering") return false;
    const src = pc.sourceId ?? pc.source;
    const tgt = pc.targetId ?? pc.target;
    return visible.has(src) && visible.has(tgt);
  });
}

/** Short address summary for a node, or "" when it carries no addressing. */
export function addressLabel(node) {
  const cfg = node?.config || {};
  const bits = [];
  if (cfg.cidr) bits.push(cfg.cidr);
  if (cfg.availabilityZone) bits.push(cfg.availabilityZone);
  return bits.join(" · ");
}
